import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "timeFields",
    "timeToggle",
    "perDayCheckbox",
    "perDayInfo",
    "timespans",
    "removeTimespan",
    "timespanEmployees",
  ]

  connect() {
    this.updateTimeFieldsVisibility()
    this.updatePerDayInfo()
    this.updateRowControls()
  }

  toggleTimeFields() {
    const isChecked = this.timeToggleTarget.checked
    for (const fields of this.timeFieldsTargets) {
      fields.classList.toggle("hidden", !isChecked)
    }
    if (!isChecked) this.clearTimeFields()
  }

  // The pickers' submitted values live in their hidden fields, matched by the tail of the form
  // field name so they are found in every period row. The element carrying the id is the visible
  // half — for a time field clearing that would blank the text while still posting the old time,
  // and for a date field it holds d.m.Y, not ISO.
  fields(attribute) {
    const form = this.element.closest("form") ?? this.element
    return Array.from(form.querySelectorAll(`input[name$="[${attribute}]"][name^="task["]`))
  }

  // The first period row is the one the per-day preview and the site dates work from.
  firstField(attribute) {
    return this.fields(attribute)[0]
  }

  clearTimeFields() {
    for (const attribute of ["scheduled_start_time", "scheduled_end_time"]) {
      for (const field of this.fields(attribute)) {
        field.value = ""
        field.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }
  }

  updateTimeFieldsVisibility() {
    if (!this.hasTimeToggleTarget || this.timeFieldsTargets.length === 0) return

    const hasTime = ["scheduled_start_time", "scheduled_end_time"].some((attribute) =>
      this.fields(attribute).some((field) => field.value),
    )

    this.timeToggleTarget.checked = hasTime
    for (const fields of this.timeFieldsTargets) {
      fields.classList.toggle("hidden", !hasTime)
    }
  }

  onRowsChanged() {
    this.updateRowControls()
    this.updatePerDayInfo()
  }

  // A task always has at least one period, so the only row's remove button is hidden. A new row
  // inherits the current state of the time toggle rather than the state it was rendered with.
  // Headings are numbered here rather than on render because a removed row is only hidden, so
  // the server-rendered numbers would keep the gap the removed row left behind.
  updateRowControls() {
    const rows = this.visibleRows()
    for (const button of this.removeTimespanTargets) {
      button.classList.toggle("hidden", rows.length <= 1)
    }
    rows.forEach((row, index) => {
      const title = row.querySelector("[data-timespan-title]")
      if (title) title.textContent = title.dataset.timespanTitle.replace("%{number}", index + 1)
    })
    // On a single-period task the whole-task select already says who works it, so the
    // per-period one is noise. A row that already has people picked keeps it visible —
    // hiding it would conceal assignments made on the board or through the API.
    for (const field of this.timespanEmployeesTargets) {
      const picked = field.querySelector("select")?.selectedOptions.length > 0
      field.classList.toggle("hidden", rows.length <= 1 && !picked)
    }
    if (this.hasTimeToggleTarget) {
      for (const fields of this.timeFieldsTargets) {
        fields.classList.toggle("hidden", !this.timeToggleTarget.checked)
      }
    }
  }

  visibleRows() {
    if (!this.hasTimespansTarget) return []

    return Array.from(this.timespansTarget.querySelectorAll("[data-timespan-row]")).filter(
      (row) => row.style.display !== "none" && !row.closest("template"),
    )
  }

  updatePerDayInfo() {
    if (!this.hasPerDayCheckboxTarget || !this.hasPerDayInfoTarget) return

    const isChecked = this.perDayCheckboxTarget.checked
    // Matched by name, so these are the pickers' hidden fields and the value is always ISO.
    // Per-day creation works from the first period row.
    const startDate = this.parseIsoDate(this.firstField("start_date")?.value)
    const endDate = this.parseIsoDate(this.firstField("end_date")?.value)

    if (isChecked && startDate && endDate && !this.firstRowOpenEnded()) {
      const dayCount = this.calculateDays(startDate, endDate)
      if (dayCount > 1) {
        // Use localized text from data attribute
        const textTemplate =
          this.perDayCheckboxTarget.dataset.tasksCreatedText || "%{count} Tasks werden erstellt"
        this.perDayInfoTarget.textContent = textTemplate.replace("%{count}", dayCount)
        this.perDayInfoTarget.classList.remove("hidden")
      } else {
        this.perDayInfoTarget.classList.add("hidden")
      }
    } else {
      this.perDayInfoTarget.classList.add("hidden")
    }
  }

  parseIsoDate(dateStr) {
    if (!dateStr) return null

    const date = new Date(`${dateStr}T00:00:00Z`)
    return isNaN(date) ? null : date
  }

  calculateDays(startDate, endDate) {
    const diffTime = Math.abs(endDate - startDate)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
    return diffDays
  }

  onDateChange() {
    this.updatePerDayInfo()
  }

  // "Offenes Ende" swaps the row's end-date picker for a plain "offen". The picker keeps its
  // value: while the box is ticked the model ignores it, and unticking brings the date back.
  toggleOpenEnd(event) {
    const row = event.target.closest("[data-timespan-row]")
    if (!row) return

    const openEnd = event.target.checked
    row.querySelector("[data-open-end-date]")?.classList.toggle("hidden", openEnd)
    row.querySelector("[data-open-end-label]")?.classList.toggle("hidden", !openEnd)
    this.updatePerDayInfo()
  }

  // Per-day creation needs a last day; an open-ended first period has none.
  firstRowOpenEnded() {
    const form = this.element.closest("form") ?? this.element
    return (
      form.querySelector('input[type="checkbox"][name$="[open_end]"][name^="task["]')?.checked ??
      false
    )
  }

  onPerDayChange() {
    this.updatePerDayInfo()
  }
}
